import {
  cachedHasAccessForBranch,
  cachedHasAccessForBranchDepartment,
  cachedHasAccessForCompany,
  cachedHasAccessForCompanyDepartment,
  hasAccessForBranchAsync,
  hasAccessForBranchDepartmentAsync,
  hasAccessForCompanyAsync,
  hasAccessForCompanyDepartmentAsync,
} from "CW1/ApplicationCheckpointService";
import { getAvailableResult, getNotLoadedResult } from "DependencyResult";
import type { DependencyAccessMixedValue, DependencyVisitorContext } from "DependencyVisitorContext";
import { promiseObserver } from "KnockoutExtensions";
import { assertIsString } from "StringUtils";
import userSession from "UserSession";
import type { Entity } from "breeze-client";

export function hasAccessForBranch<Mode>(
  [checkpoint, companyPK, branchPK]: unknown[],
  _entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(companyPK);
  assertIsString(branchPK);

  return hasAccess(context, cachedHasAccessForBranch(checkpoint, companyPK, branchPK), () =>
    hasAccessForBranchAsync(checkpoint, companyPK, branchPK),
  );
}

export function hasAccessForBranchDepartment<Mode>(
  [checkpoint, companyPK, branchPK, departmentPK]: unknown[],
  _entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(companyPK);
  assertIsString(branchPK);
  assertIsString(departmentPK);

  return hasAccess(context, cachedHasAccessForBranchDepartment(checkpoint, companyPK, branchPK, departmentPK), () =>
    hasAccessForBranchDepartmentAsync(checkpoint, companyPK, branchPK, departmentPK),
  );
}

export function hasAccessForCompany<Mode>(
  [checkpoint, companyPK]: unknown[],
  _entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(companyPK);

  return hasAccess(context, cachedHasAccessForCompany(checkpoint, companyPK), () =>
    hasAccessForCompanyAsync(checkpoint, companyPK),
  );
}

export function hasAccessForCompanyDepartment<Mode>(
  [checkpoint, companyPK, departmentPK]: unknown[],
  _entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(companyPK);
  assertIsString(departmentPK);

  return hasAccess(context, cachedHasAccessForCompanyDepartment(checkpoint, companyPK, departmentPK), () =>
    hasAccessForCompanyDepartmentAsync(checkpoint, companyPK, departmentPK),
  );
}

export function hasAccessForLogonBranch<Mode>(
  [checkpoint, branchPK]: unknown[],
  entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(branchPK);

  const { loggedOnUser } = userSession.sessionData();
  if (!loggedOnUser || branchPK !== loggedOnUser.branchPK) {
    return getAvailableResult(false);
  }

  return hasAccessForBranch<Mode>([checkpoint, loggedOnUser.companyPK, branchPK], entity, context);
}

export function hasAccessForLogonBranchDepartment<Mode>(
  [checkpoint, branchPK, departmentPK]: unknown[],
  entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(branchPK);
  assertIsString(departmentPK);

  const { loggedOnUser } = userSession.sessionData();
  if (!loggedOnUser || branchPK !== loggedOnUser.branchPK || departmentPK !== loggedOnUser.departmentPK) {
    return getAvailableResult(false);
  }

  return hasAccessForBranchDepartment<Mode>(
    [checkpoint, loggedOnUser.companyPK, branchPK, departmentPK],
    entity,
    context,
  );
}

export function hasAccessForLogonCompany<Mode>(
  [checkpoint, companyPK]: unknown[],
  entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(companyPK);

  const { loggedOnUser } = userSession.sessionData();
  if (!loggedOnUser || companyPK !== loggedOnUser.companyPK) {
    return getAvailableResult(false);
  }

  return hasAccessForCompany<Mode>([checkpoint, companyPK], entity, context);
}

export function hasAccessForLogonCompanyDepartment<Mode>(
  [checkpoint, companyPK, departmentPK]: unknown[],
  entity: Entity,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean> {
  assertIsString(checkpoint);
  assertIsString(companyPK);
  assertIsString(departmentPK);

  const { loggedOnUser } = userSession.sessionData();
  if (!loggedOnUser || companyPK !== loggedOnUser.companyPK || departmentPK !== loggedOnUser.departmentPK) {
    return getAvailableResult(false);
  }

  return hasAccessForCompanyDepartment<Mode>([checkpoint, companyPK, departmentPK], entity, context);
}

function hasAccess<Mode>(
  context: DependencyVisitorContext<Mode>,
  cachedHasAccess: boolean | undefined,
  hasAccessAsync: () => Promise<boolean>,
): DependencyAccessMixedValue<Mode, boolean> {
  if (cachedHasAccess !== undefined) {
    return getAvailableResult(cachedHasAccess);
  }

  return context.strategy.wait(hasAccessAsync(), (result) => {
    if (typeof result === "boolean") {
      return getAvailableResult(result);
    } else {
      promiseObserver(result).read();
      return getNotLoadedResult();
    }
  });
}
